import { h, render, Fragment } from 'preact';
import { useMemo, useState, useEffect, useRef } from 'preact/hooks';
import ProductGallery from './product-gallery';
import ProductGalleryZoom from './product-gallery-zoom';
import ProductForm from './product-form';
import {
  getProductByHandle,
  getProductsByTag,
  decodeId,
  findGetParameter,
} from '../utils/storefront/get-products';
import { useWindowSize } from '../utils/window-size';
import SpecificationsFlyout from './product-specifications-flyout';
import ExploreMoreFlyout from './explore-more-flyout';
import AKOAFlyout from './product-akoa-flyout';

export default (element, data) => {
  const Product = (props) => {
    // Lookup variant based on selected options

    const isGift = props?.isGift;
    const isApparel = props?.isApparel;
    const isSimple = props?.isSimple;
    const initialProduct =
      isGift || isSimple
        ? props?.data?.productByHandle
        : props?.data?.products?.nodes?.find(
            (v) => decodeId(v.id, 'Product') === props.id,
          );

    const initialVariant = findGetParameter('variant')
      ? initialProduct?.variants?.edges.find(
          (v) =>
            decodeId(v.node.id, 'ProductVariant') ===
            parseInt(findGetParameter('variant'), 10),
        ).node
      : initialProduct?.variants?.edges[0]?.node;

    useEffect(() => {
      const productInitEvent = new CustomEvent('product:init', {
        detail: {
          payload: {
            product: initialProduct,
            variant: initialVariant,
            isGift,
            isSimple,
          },
        },
      });
      document.dispatchEvent(productInitEvent);
    }, [initialProduct, initialVariant, isGift, isSimple]);

    const [currentProduct, setCurrentProduct] = useState(initialProduct);
    const [currentVariant, setCurrentVariant] = useState(initialVariant);
    const [initZoom, setInitZoom] = useState(false);
    const { isTablet } = useWindowSize();
    const [isOpen, setIsOpen] = useState(false);
    const [isOpenAkoa, setIsOpenAkoa] = useState(false);
    const [isOpenExplore, setIsOpenExplore] = useState(false);
    const [formSubmited, setFormSubmited] = useState(
      !!sessionStorage.getItem('accepted_akoa'),
    );

    const membershipData = useMemo(
      () =>
        JSON.parse(
          element.querySelector('[data-membership-data]')?.textContent,
        ),
      [],
    );
    const sectionData = useMemo(
      () =>
        JSON.parse(element.querySelector('[data-section-data]')?.textContent),
      [],
    );
    const benefitsData = useMemo(
      () =>
        JSON.parse(element.querySelector('[data-benefits-data]')?.textContent),
      [],
    );

    const shippingMessages = useMemo(
      () => JSON.parse(element.querySelector('[data-shipping]')?.textContent),
      [],
    );

    const gwpThreshold = useMemo(
      () =>
        JSON.parse(element.querySelector('[data-gwp-threshold]')?.textContent),
      [],
    );

    const productGalleryZoom = useMemo(
      () => (
        <ProductGalleryZoom
          currentProduct={currentProduct}
          initZoom={initZoom}
          setInitZoom={setInitZoom}
        />
      ),
      [currentProduct, initZoom],
    );

    const akoaFlyout = useMemo(
      () => (
        <AKOAFlyout
          element={element}
          isOpenAkoa={isOpenAkoa}
          setIsOpenAkoa={setIsOpenAkoa}
          formSubmited={formSubmited}
          setFormSubmited={setFormSubmited}
        />
      ),
      [formSubmited, isOpenAkoa],
    );

    const alreadyRendered = useRef(false);

    // eslint-disable-next-line max-len
    const specificationsData = useMemo(
      () => currentProduct?.specsMetafields?.filter((p) => p),
      [currentProduct],
    );

    useEffect(() => {
      if (!alreadyRendered.current) {
        alreadyRendered.current = true;
        return;
      }

      const productUpdatedEvent = new CustomEvent('product:updated', {
        detail: {
          payload: {
            product: currentProduct,
            variant: currentVariant,
          },
        },
      });

      if (!initialProduct) {
        // eslint-disable-next-line no-console
        console.error(
          'Theme Error: Please ensure this product is added to Online Store and Theme Access sales channels',
        );
        return;
      }

      document.dispatchEvent(productUpdatedEvent);
    }, [currentProduct, currentVariant, initialProduct]);

    return (
      <>
        <div
          className={`grid grid-cols-12 items-start relative animate-fadein ${
            initZoom ? 'z-11' : 'z-2'
          }`}
        >
          {!isTablet && productGalleryZoom}
          <ProductGallery
            currentVariant={currentVariant}
            currentProduct={currentProduct}
            setInitZoom={setInitZoom}
            isTablet={isTablet}
            isGift={isGift}
          />
          <ProductForm
            currentProduct={currentProduct}
            currentVariant={currentVariant}
            allProducts={props}
            membershipData={membershipData}
            specificationsLength={specificationsData?.length}
            specificationsData={specificationsData}
            benefitsData={benefitsData}
            sectionData={sectionData}
            element={element}
            data={data}
            setCurrentProduct={setCurrentProduct}
            setCurrentVariant={setCurrentVariant}
            isOpen={isOpen}
            isOpenAkoa={isOpenAkoa}
            isOpenExplore={isOpenExplore}
            setIsOpen={setIsOpen}
            setIsOpenAkoa={setIsOpenAkoa}
            setIsOpenExplore={setIsOpenExplore}
            isGift={isGift}
            isApparel={isApparel}
            isSimple={isSimple}
            formSubmited={formSubmited}
            setFormSubmited={setFormSubmited}
            shippingMessages={shippingMessages}
            gwpThreshold={gwpThreshold.value}
          />
        </div>
        <SpecificationsFlyout
          specificationsData={specificationsData}
          element={element}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
        <ExploreMoreFlyout
          benefitsData={benefitsData}
          currentProduct={currentProduct}
          isOpen={isOpenExplore}
          setIsOpen={setIsOpenExplore}
        />
        {isOpenAkoa && akoaFlyout}
      </>
    );
  };

  const init = async () => {
    const products =
      data.isGift || data.isSimple
        ? await getProductByHandle(data.handle)
        : await getProductsByTag(data.style_tag);
    const dataWithProducts = {
      ...data,
      ...products,
    };
    if (products) render(<Product {...dataWithProducts} />, element);
  };

  init();
};
